import { useNavigate } from "react-router-dom";
import { Home, Clock, Shield, CheckCircle, MapPin, Phone, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { toast } from "sonner";

const cities = ["Bengaluru", "Mumbai", "Delhi", "Chennai", "Pune", "Hyderabad", "Kolkata", "Ahmedabad", "Jaipur", "Lucknow"];

const steps = [
  { icon: "1️⃣", title: "Book Online", desc: "Select your tests and choose home collection at checkout" },
  { icon: "2️⃣", title: "Confirm Slot", desc: "Pick a convenient date and time (7AM–9PM, 7 days a week)" },
  { icon: "3️⃣", title: "Sample Pickup", desc: "Our trained phlebotomist arrives at your doorstep" },
  { icon: "4️⃣", title: "Get Reports", desc: "Digital reports on WhatsApp & email in 4–24 hours" },
];

const faqs = [
  { q: "Is home collection free?", a: "Home collection is free for orders above ₹500. A nominal fee of ₹100 is charged for orders below ₹500." },
  { q: "How do I prepare for sample collection?", a: "For most tests, fasting is required for 8-12 hours. Your confirmation email will include specific preparation instructions." },
  { q: "Are the phlebotomists certified?", a: "Yes, all our phlebotomists are trained, certified, and carry proper identification. They follow strict hygiene protocols." },
  { q: "Can I track when the phlebotomist will arrive?", a: "Yes, you'll receive real-time SMS updates on the phlebotomist's arrival time." },
  { q: "How will I receive my reports?", a: "Reports are delivered via email and WhatsApp. You can also download them from your account dashboard." },
];

export default function HomeCollectionPage() {
  const navigate = useNavigate();
  const [pincode, setPincode] = useState("");
  const [checked, setChecked] = useState<boolean | null>(null);

  const checkPincode = () => {
    if (!pincode || pincode.length < 6) { toast.error("Enter a valid 6-digit pincode"); return; }
    // Simulate: all pincodes are serviceable in this demo
    setChecked(true);
    toast.success("Great! Home collection is available at your location.");
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/5 to-primary/10 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 text-sm font-medium px-3 py-1.5 rounded-full mb-4">
              <Home className="h-4 w-4" /> Free Home Collection Above ₹500
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">Lab Tests at<br /><span className="text-primary">Your Doorstep</span></h1>
            <p className="text-muted-foreground text-lg mb-6">
              Skip the waiting rooms. Our certified phlebotomists come to you at your preferred time. Safe, hygienic, and hassle-free.
            </p>
            <Button size="lg" onClick={() => navigate("/tests")}>Book Home Collection</Button>
          </div>
          <img
            src="https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&h=400&fit=crop"
            alt="Home collection"
            className="rounded-2xl shadow-xl w-full h-[350px] object-cover"
          />
        </div>
      </section>

      {/* Check availability */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-lg mx-auto text-center">
          <h2 className="text-2xl font-bold text-foreground mb-2">Check Availability</h2>
          <p className="text-muted-foreground mb-6">Enter your pincode to check if home collection is available in your area</p>
          <div className="flex gap-3">
            <Input
              placeholder="Enter 6-digit pincode"
              value={pincode}
              onChange={e => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="flex-1"
            />
            <Button onClick={checkPincode}>Check</Button>
          </div>
          {checked !== null && (
            <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 text-sm font-medium ${checked ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
              <CheckCircle className="h-4 w-4 shrink-0" />
              {checked ? `Home collection available at pincode ${pincode}!` : "Sorry, we don't serve this area yet."}
            </div>
          )}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-muted/30 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center text-foreground mb-10">How It Works</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {steps.map(step => (
              <div key={step.title} className="text-center">
                <div className="text-4xl mb-3">{step.icon}</div>
                <h3 className="font-semibold text-foreground mb-1">{step.title}</h3>
                <p className="text-xs text-muted-foreground">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { icon: Shield, title: "100% Safe & Hygienic", desc: "PPE kits, sterile equipment, and safety protocols" },
            { icon: Clock, title: "Flexible Timings", desc: "7AM to 9PM, 7 days a week including holidays" },
            { icon: Star, title: "Certified Staff", desc: "Background-verified, trained phlebotomists" },
          ].map(feat => (
            <div key={feat.title} className="p-6 bg-card border border-border rounded-xl text-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <feat.icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-foreground mb-1">{feat.title}</h3>
              <p className="text-sm text-muted-foreground">{feat.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Cities */}
      <section className="bg-muted/30 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-xl font-bold text-foreground mb-6">Available in 50+ Cities</h2>
          <div className="flex flex-wrap justify-center gap-2">
            {cities.map(city => (
              <span key={city} className="flex items-center gap-1 px-3 py-1.5 bg-card border border-border rounded-full text-sm text-foreground">
                <MapPin className="h-3 w-3 text-primary" />{city}
              </span>
            ))}
            <span className="px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">+ 40 more</span>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="text-2xl font-bold text-foreground mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {faqs.map(faq => (
            <div key={faq.q} className="bg-card border border-border rounded-xl p-5">
              <p className="font-medium text-foreground mb-2">{faq.q}</p>
              <p className="text-sm text-muted-foreground">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
